import Fuse from 'fuse.js';
import type { AgendaItem, TranscriptSegment, MatchResult } from '@/types';

// Tuning parameters - adjusted based on real-world demo call analysis
const MATCH_THRESHOLD = 0.45;        // Raised from 0.35 - too many false positives
const CONTEXT_WINDOW_SIZE = 200;     // Increased from 150 - captures ~80 sec at 150wpm
const FUSE_THRESHOLD = 0.4;          // Tightened from 0.5 - was too fuzzy
const DEBUG_MODE = true;             // Log match scores to console

// Order-awareness parameters
const ORDER_PENALTY_START = 3;       // Start applying penalty when item is 3+ positions ahead
const ORDER_PENALTY_MAX = 0.25;      // Reduced from 0.4 - was too aggressive for natural conversation flow
const ORDER_PENALTY_SCALE = 10;      // Items 10+ positions ahead get max penalty

// Keyword coverage requirements
const MIN_KEYWORD_COVERAGE = 0.30;   // Raised from 0.20 - need meaningful overlap
const MIN_KEYWORDS_FOR_COVERAGE = 3; // Only apply coverage requirement if item has 3+ keywords

// Common synonyms and related terms for business/demo contexts
const SYNONYMS: Record<string, string[]> = {
  // Actions
  'show': ['demonstrate', 'display', 'present', 'walk through', 'demo', 'showing'],
  'explain': ['describe', 'discuss', 'cover', 'talk about', 'go over', 'walk through'],
  'create': ['build', 'make', 'set up', 'configure', 'add'],
  'update': ['modify', 'change', 'edit', 'adjust'],
  'delete': ['remove', 'clear', 'drop'],
  'compare': ['comparing', 'comparison', 'versus', 'against', 'vs'],
  'powered': ['uses', 'leverages', 'built on', 'runs on', 'using'],

  // Business terms
  'dashboard': ['overview', 'home', 'main screen', 'landing page'],
  'report': ['analytics', 'metrics', 'data', 'insights', 'stats', 'statistics'],
  'integration': ['connect', 'sync', 'link', 'integrate'],
  'workflow': ['process', 'automation', 'flow'],
  'feature': ['functionality', 'capability', 'option'],
  'customer': ['client', 'user', 'account'],
  'team': ['users', 'members', 'organization', 'org'],
  'outcomes': ['results', 'output', 'impact', 'effects'],
  'adoption': ['usage', 'rollout', 'uptake', 'using', 'use'],
  'returning': ['coming back', 'repeat', 'recurring', 'return'],

  // Tech terms
  'api': ['endpoint', 'interface', 'integration'],
  'database': ['data', 'storage', 'db'],
  'deployment': ['deploy', 'release', 'ship', 'launch'],
  'configuration': ['config', 'settings', 'setup', 'preferences'],
  'daemon': ['background process', 'service', 'agent', 'background'],
  'backfill': ['historical', 'historical data', 'past data', 'import history', 'retroactive'],

  // AI/ML terms
  'llm': ['llms', 'large language model', 'ai', 'ai model', 'ai modeling', 'language model', 'gpt', 'claude', 'chatgpt', 'artificial intelligence'],
  'ai': ['artificial intelligence', 'machine learning', 'ml', 'llm', 'llms', 'ai model', 'ai modeling'],
  'model': ['modeling', 'models'],

  // Business/Startup terms
  'startup': ['startups', 'business', 'businesses', 'company', 'companies', 'venture', 'ventures'],
  'business': ['businesses', 'company', 'companies', 'startup', 'startups', 'enterprise', 'organization'],
  'company': ['companies', 'business', 'businesses', 'startup', 'startups', 'firm', 'organization'],

  // Common phrases
  'how to': ['how we', 'the way to', 'steps to'],
  'benefits': ['advantages', 'value', 'why'],
  'pricing': ['cost', 'price', 'plans', 'tiers'],
  'security': ['secure', 'protection', 'privacy', 'compliance'],
  'performance': ['speed', 'fast', 'efficient', 'optimization'],
};

// Custom synonyms type for org vocabulary
export type CustomSynonyms = Record<string, string[]>;

// Expand a word to include its synonyms (with optional custom synonyms)
function expandWithSynonyms(word: string, customSynonyms?: CustomSynonyms): string[] {
  const lower = word.toLowerCase();
  const result = [lower];

  // Check hardcoded synonyms first
  if (SYNONYMS[lower]) {
    result.push(...SYNONYMS[lower]);
  }

  // Check if this word is a value in any hardcoded synonym group
  for (const [key, values] of Object.entries(SYNONYMS)) {
    if (values.includes(lower)) {
      result.push(key, ...values.filter(v => v !== lower));
    }
  }

  // Check custom/org synonyms if provided
  if (customSynonyms) {
    if (customSynonyms[lower]) {
      result.push(...customSynonyms[lower]);
    }

    // Check if this word is a value in any custom synonym group
    for (const [key, values] of Object.entries(customSynonyms)) {
      if (values.includes(lower)) {
        result.push(key, ...values.filter(v => v !== lower));
      }
    }
  }

  return [...new Set(result)];
}

// Extended agenda item with LLM-generated phrases
interface ExpandedAgendaItem extends AgendaItem {
  _expandedPhrases?: string[];
}

/**
 * Matching engine that determines if spoken words cover agenda items
 * With improved matching, debug logging, synonym support, and LLM expansion
 */
export class MatchingEngine {
  private agenda: ExpandedAgendaItem[];
  private fuse: Fuse<AgendaItem>;
  private contextWindow: string[] = [];
  private fullTranscript: string[] = []; // Keep full transcript for better matching
  private itemIndexMap: Map<string, number> = new Map(); // Map item ID to its position
  private currentProgressIndex: number = -1; // Highest covered item index
  private expandedPhrases: Map<string, string[]> = new Map(); // LLM-generated phrases per item
  private customSynonyms: CustomSynonyms | undefined; // Org-specific vocabulary
  private wordDistinctiveness: Map<string, number> = new Map(); // IDF-like scores for words
  private itemDistinctiveWords: Map<string, Set<string>> = new Map(); // Distinctive words per item

  constructor(agenda: AgendaItem[], customSynonyms?: CustomSynonyms) {
    this.agenda = agenda;
    this.customSynonyms = customSynonyms;
    this.fuse = this.buildFuseIndex(agenda);
    this.buildItemIndexMap(agenda);
    this.extractExpandedPhrases(agenda);
    this.calculateWordDistinctiveness(agenda);

    if (DEBUG_MODE) {
      console.log('🎯 MatchingEngine initialized with', agenda.length, 'items');
      if (customSynonyms && Object.keys(customSynonyms).length > 0) {
        console.log('📚 Custom vocabulary loaded:', Object.keys(customSynonyms).length, 'terms');
      }
      console.log('📋 Agenda items:', agenda.map(i => i.text));
    }
  }

  /**
   * Calculate word distinctiveness scores (IDF-like) across all agenda items
   * Words that appear in many items get lower scores, unique words get higher scores
   */
  private calculateWordDistinctiveness(agenda: AgendaItem[]): void {
    this.wordDistinctiveness.clear();
    this.itemDistinctiveWords.clear();

    // Count how many items each word appears in
    const wordItemCount: Map<string, number> = new Map();
    const itemWords: Map<string, Set<string>> = new Map();

    // Common stop words to always exclude from distinctiveness
    const stopWords = new Set([
      'the', 'a', 'an', 'and', 'or', 'but', 'is', 'are', 'was', 'were', 'be', 'been',
      'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
      'should', 'may', 'might', 'must', 'shall', 'can', 'to', 'of', 'in', 'for',
      'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through', 'during', 'before',
      'after', 'above', 'below', 'between', 'under', 'again', 'further', 'then',
      'once', 'here', 'there', 'when', 'where', 'why', 'how', 'all', 'each', 'few',
      'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own',
      'same', 'so', 'than', 'too', 'very', 'just', 'also', 'now', 'your', 'you',
      'that', 'this', 'these', 'those', 'it', 'its', 'if', 'about', 'which', 'what'
    ]);

    for (const item of agenda) {
      if (item.isGroupHeader) continue;

      const words = item.text.toLowerCase()
        .replace(/[^\w\s]/g, '')
        .split(/\s+/)
        .filter(w => w.length > 2 && !stopWords.has(w));

      const uniqueWords = new Set(words);
      itemWords.set(item.id, uniqueWords);

      for (const word of uniqueWords) {
        wordItemCount.set(word, (wordItemCount.get(word) || 0) + 1);
      }
    }

    // Calculate IDF-like distinctiveness score for each word
    const totalItems = agenda.filter(i => !i.isGroupHeader).length;
    for (const [word, count] of wordItemCount) {
      // IDF formula: log(totalItems / count)
      // Normalized to 0-1 range where 1 = unique to one item
      const distinctiveness = Math.log(totalItems / count) / Math.log(totalItems);
      this.wordDistinctiveness.set(word, Math.max(0.1, distinctiveness)); // Min 0.1 for common words
    }

    // Identify distinctive words for each item (words that appear in <= 2 items)
    for (const item of agenda) {
      if (item.isGroupHeader) continue;

      const words = itemWords.get(item.id);
      if (!words) continue;

      const distinctiveWords = new Set<string>();
      for (const word of words) {
        const count = wordItemCount.get(word) || 0;
        // A word is "distinctive" if it appears in at most 30% of items (or 2 items, whichever is higher)
        const threshold = Math.max(2, Math.ceil(totalItems * 0.3));
        if (count <= threshold) {
          distinctiveWords.add(word);
        }
      }

      this.itemDistinctiveWords.set(item.id, distinctiveWords);

      if (DEBUG_MODE && distinctiveWords.size > 0) {
        console.log(`  🔑 Distinctive words for "${item.text.substring(0, 30)}...": ${Array.from(distinctiveWords).slice(0, 5).join(', ')}`);
      }
    }
  }

  /**
   * Extract LLM-generated expanded phrases from agenda items
   */
  private extractExpandedPhrases(agenda: AgendaItem[]): void {
    this.expandedPhrases.clear();
    for (const item of agenda) {
      const expanded = (item as ExpandedAgendaItem)._expandedPhrases;
      if (expanded && expanded.length > 0) {
        this.expandedPhrases.set(item.id, expanded);
        if (DEBUG_MODE) {
          console.log(`  📝 Loaded ${expanded.length} expanded phrases for "${item.text.substring(0, 30)}..."`);
        }
      }
    }
  }

  private buildItemIndexMap(agenda: AgendaItem[]): void {
    this.itemIndexMap.clear();
    agenda.forEach((item, index) => {
      this.itemIndexMap.set(item.id, index);
    });
  }

  private buildFuseIndex(agenda: AgendaItem[]): Fuse<AgendaItem> {
    return new Fuse(agenda, {
      keys: [
        { name: 'text', weight: 0.6 },
        { name: 'keywords', weight: 0.4 },
      ],
      threshold: FUSE_THRESHOLD,
      distance: 200,              // Increased for more flexibility
      includeScore: true,
      minMatchCharLength: 2,      // Reduced from 3
      ignoreLocation: true,
      findAllMatches: true,       // Find all matches, not just best
    });
  }

  processSegment(segment: TranscriptSegment): MatchResult[] {
    const words = segment.text.toLowerCase().split(/\s+/).filter(w => w.length > 0);
    this.contextWindow.push(...words);
    this.fullTranscript.push(...words);

    // Keep sliding window
    if (this.contextWindow.length > CONTEXT_WINDOW_SIZE) {
      this.contextWindow = this.contextWindow.slice(-CONTEXT_WINDOW_SIZE);
    }

    const contextText = this.contextWindow.join(' ');
    const fullText = this.fullTranscript.join(' ');
    const matches: MatchResult[] = [];

    if (DEBUG_MODE && segment.isFinal) {
      console.log(`\n🎤 Transcript: "${segment.text}"`);
      console.log(`📊 Context window: ${this.contextWindow.length} words | Progress: item ${this.currentProgressIndex + 1}`);
    }

    for (const item of this.agenda) {
      if (item.covered || item.isGroupHeader) continue; // Skip covered items and group headers

      const itemIndex = this.itemIndexMap.get(item.id) ?? 0;
      const rawConfidence = this.calculateMatchConfidence(item, contextText, fullText, segment.text);

      // Apply order penalty for items far ahead of current progress
      const orderPenalty = this.calculateOrderPenalty(itemIndex);
      const confidence = rawConfidence * (1 - orderPenalty);

      if (DEBUG_MODE && segment.isFinal) {
        const status = confidence >= MATCH_THRESHOLD ? '✅' : '❌';
        const scoreDisplay = (confidence * 100).toFixed(1);
        const penaltyDisplay = orderPenalty > 0 ? ` (raw: ${(rawConfidence * 100).toFixed(1)}%, penalty: -${(orderPenalty * 100).toFixed(0)}%)` : '';
        console.log(`  ${status} [${scoreDisplay}%] #${itemIndex + 1} "${item.text.substring(0, 40)}..."${penaltyDisplay}`);
      }

      if (confidence >= MATCH_THRESHOLD) {
        matches.push({
          itemId: item.id,
          confidence,
          matchedText: segment.text,
        });

        // Update progress index if this item is ahead of current progress
        if (itemIndex > this.currentProgressIndex) {
          this.currentProgressIndex = itemIndex;
          if (DEBUG_MODE) {
            console.log(`  📍 Progress updated to item #${itemIndex + 1}`);
          }
        }

        if (DEBUG_MODE) {
          console.log(`  🎯 MATCHED! Item will be marked as covered`);
        }
      }
    }

    return matches;
  }

  /**
   * Calculate penalty for items that are far ahead of current progress
   * Returns 0 (no penalty) to ORDER_PENALTY_MAX based on distance
   */
  private calculateOrderPenalty(itemIndex: number): number {
    const distance = itemIndex - this.currentProgressIndex;

    // No penalty for items at or behind current progress
    if (distance <= ORDER_PENALTY_START) {
      return 0;
    }

    // Calculate penalty based on how far ahead the item is
    const effectiveDistance = distance - ORDER_PENALTY_START;
    const penaltyRatio = Math.min(effectiveDistance / ORDER_PENALTY_SCALE, 1);

    return penaltyRatio * ORDER_PENALTY_MAX;
  }

  private calculateMatchConfidence(
    item: AgendaItem,
    contextText: string,
    fullText: string,
    recentText: string
  ): number {
    // Multiple matching strategies
    const keywordScore = this.calculateKeywordScore(item.keywords, contextText);
    const synonymScore = this.calculateSynonymScore(item.keywords, contextText);
    const fuseScore = this.calculateFuseScore(item.id, recentText);
    const substringBonus = this.calculateSubstringBonus(item.text, contextText);
    const fullTextBonus = this.calculateSubstringBonus(item.text, fullText) * 0.5; // Check full transcript too
    const phraseScore = this.calculatePhraseScore(item.text, contextText);
    const expandedPhraseScore = this.calculateExpandedPhraseScore(item.id, contextText);

    // Weighted combination - more weight on direct matches
    // If we have LLM-expanded phrases, give them significant weight
    const hasExpandedPhrases = this.expandedPhrases.has(item.id);
    let combined: number;

    if (hasExpandedPhrases) {
      // With LLM expansion: give expanded phrases 15% weight, reduce others proportionally
      combined = (
        (keywordScore * 0.20) +
        (synonymScore * 0.10) +
        (fuseScore * 0.15) +
        (substringBonus * 0.20) +
        (fullTextBonus * 0.05) +
        (phraseScore * 0.10) +
        (expandedPhraseScore * 0.20)
      );
    } else {
      // Without expansion: original weights
      combined = (
        (keywordScore * 0.25) +
        (synonymScore * 0.15) +
        (fuseScore * 0.2) +
        (substringBonus * 0.25) +
        (fullTextBonus * 0.05) +
        (phraseScore * 0.1)
      );
    }

    // Apply penalty for low keyword coverage on multi-keyword items
    // This prevents single-word matches like "adoption" matching different contexts
    if (item.keywords.length >= MIN_KEYWORDS_FOR_COVERAGE) {
      const keywordCoverage = this.calculateKeywordCoverage(item.keywords, contextText);
      if (keywordCoverage < MIN_KEYWORD_COVERAGE) {
        // Reduce score significantly if only 1-2 words match out of many
        const coveragePenalty = 0.5 * (1 - keywordCoverage / MIN_KEYWORD_COVERAGE);
        combined *= (1 - coveragePenalty);

        if (DEBUG_MODE) {
          console.log(`    ⚠️ Low keyword coverage: ${(keywordCoverage * 100).toFixed(0)}% (${Math.round(keywordCoverage * item.keywords.length)}/${item.keywords.length} keywords) - penalty applied`);
        }
      }
    }

    // Apply penalty when distinctive words don't match
    // This handles repetitive phrases like "your proposal should..." - if an item has
    // unique words that differentiate it, we require those to match
    const distinctiveMatch = this.calculateDistinctiveWordMatch(item.id, contextText);
    if (distinctiveMatch.shouldPenalize) {
      // Apply a significant penalty (up to 40%) when distinctive words are missing
      const distinctivePenalty = 0.4 * (1 - distinctiveMatch.score);
      combined *= (1 - distinctivePenalty);

      if (DEBUG_MODE) {
        const distinctiveWords = this.itemDistinctiveWords.get(item.id);
        console.log(`    ⚠️ Missing distinctive words: only ${(distinctiveMatch.score * 100).toFixed(0)}% matched (${Array.from(distinctiveWords || []).slice(0, 3).join(', ')}...) - penalty applied`);
      }
    }

    return Math.min(combined, 1);
  }

  private calculateKeywordCoverage(keywords: string[], spokenText: string): number {
    if (keywords.length === 0) return 1;

    const spokenWords = new Set(spokenText.toLowerCase().split(/\s+/));
    let matchCount = 0;

    for (const keyword of keywords) {
      // Check direct match
      if (spokenWords.has(keyword)) {
        matchCount++;
        continue;
      }

      // Check if spoken text contains the keyword as substring
      if (spokenText.toLowerCase().includes(keyword)) {
        matchCount += 0.5;
      }
    }

    return matchCount / keywords.length;
  }

  private calculateKeywordScore(keywords: string[], spokenText: string): number {
    if (keywords.length === 0) return 0;

    const spokenWords = new Set(spokenText.toLowerCase().split(/\s+/));
    let matches = 0;

    for (const keyword of keywords) {
      // Direct match
      if (spokenWords.has(keyword)) {
        matches++;
        continue;
      }

      // Partial match (word contains keyword or vice versa)
      for (const word of spokenWords) {
        if (word.length > 2 && keyword.length > 2) {
          if (word.includes(keyword) || keyword.includes(word)) {
            matches += 0.8;
            break;
          }
          // Levenshtein-like: first 3+ chars match
          if (word.length >= 4 && keyword.length >= 4) {
            if (word.substring(0, 4) === keyword.substring(0, 4)) {
              matches += 0.6;
              break;
            }
          }
        }
      }
    }

    return Math.min(matches / keywords.length, 1);
  }

  private calculateSynonymScore(keywords: string[], spokenText: string): number {
    if (keywords.length === 0) return 0;

    const spokenWords = new Set(spokenText.toLowerCase().split(/\s+/));
    let matches = 0;

    for (const keyword of keywords) {
      // Pass custom synonyms to expansion function
      const synonyms = expandWithSynonyms(keyword, this.customSynonyms);

      for (const synonym of synonyms) {
        // Check single word synonyms
        if (spokenWords.has(synonym)) {
          matches += (synonym === keyword) ? 1 : 0.8;
          break;
        }

        // Check multi-word synonyms
        if (synonym.includes(' ') && spokenText.includes(synonym)) {
          matches += 0.8;
          break;
        }
      }
    }

    return Math.min(matches / keywords.length, 1);
  }

  private calculateFuseScore(itemId: string, searchText: string): number {
    if (searchText.length < 2) return 0;

    const results = this.fuse.search(searchText);
    const match = results.find(r => r.item.id === itemId);

    if (!match || match.score === undefined) return 0;
    return 1 - match.score;
  }

  private calculateSubstringBonus(itemText: string, spokenText: string): number {
    const normalizedItem = itemText.toLowerCase().replace(/[^\w\s]/g, '');
    const normalizedSpoken = spokenText.toLowerCase().replace(/[^\w\s]/g, '');
    const itemWords = normalizedItem.split(/\s+/).filter(w => w.length > 2);

    if (itemWords.length === 0) return 0;

    let foundCount = 0;
    for (const word of itemWords) {
      if (normalizedSpoken.includes(word)) {
        foundCount++;
      }
    }

    return foundCount / itemWords.length;
  }

  private calculatePhraseScore(itemText: string, spokenText: string): number {
    // Check for consecutive word matches (phrases)
    const itemWords = itemText.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/).filter(w => w.length > 2);
    const spokenLower = spokenText.toLowerCase();

    if (itemWords.length < 2) return 0;

    let maxConsecutive = 0;

    // Check for 2-word and 3-word phrases
    for (let i = 0; i < itemWords.length - 1; i++) {
      const twoWordPhrase = `${itemWords[i]} ${itemWords[i + 1]}`;
      if (spokenLower.includes(twoWordPhrase)) {
        maxConsecutive = Math.max(maxConsecutive, 2);
      }

      if (i < itemWords.length - 2) {
        const threeWordPhrase = `${itemWords[i]} ${itemWords[i + 1]} ${itemWords[i + 2]}`;
        if (spokenLower.includes(threeWordPhrase)) {
          maxConsecutive = Math.max(maxConsecutive, 3);
        }
      }
    }

    // Score based on phrase length found
    if (maxConsecutive >= 3) return 1;
    if (maxConsecutive >= 2) return 0.7;
    return 0;
  }

  /**
   * Calculate score based on matching distinctive words for an item
   * Returns a score from 0-1 based on how many of the item's distinctive words were matched
   * Also returns whether we should apply a penalty for missing distinctive words
   */
  private calculateDistinctiveWordMatch(itemId: string, spokenText: string): { score: number; shouldPenalize: boolean } {
    const distinctiveWords = this.itemDistinctiveWords.get(itemId);

    // If item has no distinctive words, no penalty
    if (!distinctiveWords || distinctiveWords.size === 0) {
      return { score: 0, shouldPenalize: false };
    }

    const spokenWords = new Set(spokenText.toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/));
    let matchedDistinctive = 0;
    let totalDistinctive = distinctiveWords.size;

    for (const word of distinctiveWords) {
      // Check direct match
      if (spokenWords.has(word)) {
        matchedDistinctive++;
        continue;
      }

      // Check partial/substring match
      for (const spoken of spokenWords) {
        if (spoken.length > 3 && word.length > 3) {
          if (spoken.includes(word) || word.includes(spoken)) {
            matchedDistinctive += 0.5;
            break;
          }
        }
      }
    }

    const matchRatio = matchedDistinctive / totalDistinctive;

    // Should penalize if:
    // 1. Item has at least 2 distinctive words
    // 2. Less than 40% of distinctive words matched
    const shouldPenalize = totalDistinctive >= 2 && matchRatio < 0.4;

    return { score: matchRatio, shouldPenalize };
  }

  /**
   * Calculate score based on LLM-generated expanded phrases
   */
  private calculateExpandedPhraseScore(itemId: string, spokenText: string): number {
    const phrases = this.expandedPhrases.get(itemId);
    if (!phrases || phrases.length === 0) return 0;

    const spokenLower = spokenText.toLowerCase();
    let bestScore = 0;

    for (const phrase of phrases) {
      const phraseLower = phrase.toLowerCase();

      // Exact phrase match
      if (spokenLower.includes(phraseLower)) {
        bestScore = Math.max(bestScore, 1.0);
        continue;
      }

      // Partial match - check word overlap
      const phraseWords = phraseLower.split(/\s+/).filter(w => w.length > 2);
      const spokenWords = new Set(spokenLower.split(/\s+/));

      let matchedWords = 0;
      for (const word of phraseWords) {
        if (spokenWords.has(word)) {
          matchedWords++;
        }
      }

      if (phraseWords.length > 0) {
        const overlapScore = matchedWords / phraseWords.length;
        // Only count if at least half the phrase words match
        if (overlapScore >= 0.5) {
          bestScore = Math.max(bestScore, overlapScore * 0.8);
        }
      }
    }

    return bestScore;
  }

  updateAgenda(agenda: AgendaItem[]): void {
    this.agenda = agenda;
    this.fuse = this.buildFuseIndex(agenda);
    this.buildItemIndexMap(agenda);
    this.extractExpandedPhrases(agenda);

    // Update progress index based on already-covered items
    this.currentProgressIndex = -1;
    agenda.forEach((item, index) => {
      if (item.covered && index > this.currentProgressIndex) {
        this.currentProgressIndex = index;
      }
    });

    if (DEBUG_MODE) {
      console.log('🔄 Agenda updated with', agenda.length, 'items, progress at', this.currentProgressIndex + 1);
    }
  }

  /**
   * Two-pass matching: Run a full sweep of the entire transcript against uncovered items
   * This catches items that were missed in real-time due to fragmented speech
   * Returns matches that should be marked as covered
   */
  runFullTranscriptSweep(): MatchResult[] {
    if (this.fullTranscript.length === 0) return [];

    const fullText = this.fullTranscript.join(' ');
    const matches: MatchResult[] = [];

    if (DEBUG_MODE) {
      console.log(`\n🔍 Running full transcript sweep (${this.fullTranscript.length} words)...`);
    }

    // Use a lower threshold for the sweep since we have full context
    const SWEEP_THRESHOLD = MATCH_THRESHOLD * 0.85; // ~30% - full context allows lower threshold

    for (const item of this.agenda) {
      if (item.covered || item.isGroupHeader) continue;

      const itemIndex = this.itemIndexMap.get(item.id) ?? 0;

      // Calculate confidence without order penalty (full transcript = full context)
      const rawConfidence = this.calculateMatchConfidence(item, fullText, fullText, fullText);

      // Apply reduced order penalty for sweep (we're more lenient here)
      const orderPenalty = this.calculateOrderPenalty(itemIndex) * 0.5;
      const confidence = rawConfidence * (1 - orderPenalty);

      if (DEBUG_MODE) {
        const status = confidence >= SWEEP_THRESHOLD ? '✅' : '❌';
        const scoreDisplay = (confidence * 100).toFixed(1);
        console.log(`  ${status} [${scoreDisplay}%] #${itemIndex + 1} "${item.text.substring(0, 40)}..."`);
      }

      if (confidence >= SWEEP_THRESHOLD) {
        // Find the best matching segment from the transcript
        const matchedText = this.findBestMatchingSegment(item, fullText);

        matches.push({
          itemId: item.id,
          confidence,
          matchedText,
        });

        // Update progress index
        if (itemIndex > this.currentProgressIndex) {
          this.currentProgressIndex = itemIndex;
        }

        if (DEBUG_MODE) {
          console.log(`  🎯 SWEEP MATCHED! "${item.text.substring(0, 30)}..."`);
        }
      }
    }

    if (DEBUG_MODE) {
      console.log(`✅ Sweep complete: ${matches.length} new matches found`);
    }

    return matches;
  }

  /**
   * Find the best matching segment from the transcript for an item
   * Always tries to return actual spoken text, not a generic fallback
   */
  private findBestMatchingSegment(item: AgendaItem, fullText: string): string {
    const fullTextLower = fullText.toLowerCase();
    const words = fullText.split(/\s+/);

    // Strategy 1: Look for expanded phrases (best match)
    const expandedPhrases = this.expandedPhrases.get(item.id);
    if (expandedPhrases) {
      for (const phrase of expandedPhrases) {
        const idx = fullTextLower.indexOf(phrase.toLowerCase());
        if (idx !== -1) {
          const start = Math.max(0, idx - 40);
          const end = Math.min(fullText.length, idx + phrase.length + 40);
          return '...' + fullText.substring(start, end).trim() + '...';
        }
      }
    }

    // Strategy 2: Look for 2+ keyword clusters in a window
    const itemKeywords = item.keywords.filter(kw => kw.length > 2);

    for (let i = 0; i < words.length - 10; i++) {
      const window = words.slice(i, i + 20).join(' ');
      const windowLower = window.toLowerCase();

      let keywordMatches = 0;
      for (const kw of itemKeywords) {
        if (windowLower.includes(kw)) keywordMatches++;
      }

      if (keywordMatches >= 2) {
        return '...' + window + '...';
      }
    }

    // Strategy 3: Find ANY single keyword and show context around it
    for (const kw of itemKeywords) {
      const idx = fullTextLower.indexOf(kw);
      if (idx !== -1) {
        // Find word boundaries around this position
        const start = Math.max(0, idx - 60);
        const end = Math.min(fullText.length, idx + kw.length + 60);
        return '...' + fullText.substring(start, end).trim() + '...';
      }
    }

    // Strategy 4: Check item text words directly
    const itemWords = item.text.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    for (const word of itemWords) {
      const idx = fullTextLower.indexOf(word);
      if (idx !== -1) {
        const start = Math.max(0, idx - 60);
        const end = Math.min(fullText.length, idx + word.length + 60);
        return '...' + fullText.substring(start, end).trim() + '...';
      }
    }

    // Strategy 5: Last resort - show recent part of transcript where match likely occurred
    // This shouldn't happen if we matched, but just in case
    const recentWords = words.slice(-30).join(' ');
    if (recentWords.length > 10) {
      return '...' + recentWords + ' (recent context)';
    }

    return '(matched via overall similarity)';
  }

  clearContext(): void {
    this.contextWindow = [];
    this.fullTranscript = [];
    this.currentProgressIndex = -1;

    if (DEBUG_MODE) {
      console.log('🧹 Context and progress cleared');
    }
  }

  // Debug helper - call from console to see current state
  getDebugInfo(): object {
    return {
      contextWindowSize: this.contextWindow.length,
      fullTranscriptSize: this.fullTranscript.length,
      recentContext: this.contextWindow.slice(-20).join(' '),
      currentProgressIndex: this.currentProgressIndex,
      currentProgressItem: this.currentProgressIndex >= 0 ? this.agenda[this.currentProgressIndex]?.text : 'N/A',
      uncoveredItems: this.agenda.filter(i => !i.covered).map(i => `#${this.itemIndexMap.get(i.id)! + 1}: ${i.text}`),
      coveredItems: this.agenda.filter(i => i.covered).map(i => `#${this.itemIndexMap.get(i.id)! + 1}: ${i.text}`),
    };
  }
}

export function createMatchingEngine(agenda: AgendaItem[]): MatchingEngine {
  return new MatchingEngine(agenda);
}
